import {Component, Mixins, Prop} from 'vue-property-decorator';
import Modal from "@/components/Modal.vue";
import DropdownButton from '@/components/DropdownButton.vue';
import LoadingMixin from "@/mixins/LoadingMixin";
import {DateTime, DateObjectUnits} from 'luxon';
import { globalFlatpickrConfig } from '@/utils/flatPickr';
import { isEmailValid } from "@/utils/email";
import {
    SupportAvailability,
    AvailabilityScheduleOverride,
    AvailabilitySchedule,
    ScheduledCallbacks
} from './ProductsAndServicesModels';
import { HTTP } from '../services/HttpServices';
import ThemeMixin from "@/mixins/ThemeMixin";
import PhoneNumber from '@/components/PhoneNumber.vue';
import {getSupportAvailability} from "@/products-and-services/ProductsAndServicesService";

interface CallbackAvailability {
    date: DateTime;
    isAvailable: boolean;
    availableTimes: string[];
}

interface TimeSlot {
    startDateTime: DateTime;
    endDateTime: DateTime;
}

interface TimeSlotCount {
    timeSlot: TimeSlot;
    scheduledCount: number;
}

interface TimeSlotMap {
    dateTime: DateTime;
    timeSlots: TimeSlotCount[]
}


@Component({
    components: {
        DropdownButton,
        Modal,
        PhoneNumber
    }
})
export default class CallScheduleForm extends Mixins(LoadingMixin, ThemeMixin) {
    @Prop({ type: Boolean, required: false, default: false })
    private shouldApplyModalMargins!: boolean;
    @Prop({ type: Boolean, required: false, default: false })
    private isReschedule!: boolean;
    @Prop({ type: Boolean, required: false, default: false })
    private showCancelButton!: boolean;

    private errors = {};
    private data = {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        date: '',
        timeRange: '' };
    private timeline: string[] = [];
    private marginValueToApply = '28px';
    private disable = false;
    private availability = {} as SupportAvailability;
    private flatPickerConfig = {...globalFlatpickrConfig};
    // if this changes there will be a lot of edge cases to resolve
    private callTimeHoursRange = 2;
    private callbackAvailability: CallbackAvailability[] = [];

    get formMargins() {
        return this.shouldApplyModalMargins ? `margin: 0 ${this.marginValueToApply};` : `margin: ${this.marginValueToApply} 0;`
    }

    get buttonText() {
        return this.isReschedule ? 'Reschedule' : 'Schedule'
    }

    get ctaMargins() {
        return this.shouldApplyModalMargins && `margin: ${this.marginValueToApply};`
    }

    get formInputs() {
        return Object.entries(this.data).map(([key, value]) =>
        ({
            label: this.upperFirstChar(key),
            name: this.convertToKebabCase(key),
            type: key !== 'date' && key !== 'timeRange' && key !== 'phoneNumber' ? 'input' : key,
            value, key
        }));
    }

    get bekHoursRange() {
        return this.timeline;
    }

    async created() {
        this.availability = await getSupportAvailability();
         this.buildAvailabilityObject();
        this.flatPickerConfig = this.getflatPickerConfig();
        const customerInfo = this.$store.getters.getCustomerInfo;
        if (customerInfo) {
            this.data.firstName = customerInfo.firstName || '';
            this.data.lastName = customerInfo.lastName || '';
            this.data.phoneNumber = customerInfo.mobilePhoneNumber || customerInfo.landLinePhoneNumber || '';
            this.data.email = customerInfo.email || '';
        }
        this.disable = Boolean(this.data.firstName && this.data.lastName && this.data.phoneNumber && this.data.email);
    }

    getflatPickerConfig() {
        const today = DateTime.local().startOf('day').toJSDate();
        return {
            ...globalFlatpickrConfig,
            maxDate: DateTime.local().plus({days: this.availability.callBackDayRange}).toISO(),
            disable: [
                // Remove days in past and today
                (date: Date) => date <= today,

                // Remove days of week not in schedule
                (date: Date) => {
                    return this.availability.schedule
                        .find(d => d.dayOfWeek === date.getDay() && d.startTime !== "" && d.endTime !== "") === undefined;
                },

                // Remove days of week in override if all day
                (date: Date) => {
                    return this.availability.overrides
                        .find(d => {
                            const startTime = DateTime.fromISO(d.overrideStartTime, {zone: 'America/Chicago'});
                            const endTime = DateTime.fromISO(d.overrideEndTime, {zone: 'America/Chicago'});
                            return this.isSameDay(DateTime.fromJSDate(date, {zone: 'America/Chicago'}), startTime) && this.availabilityDateIsAllDay(startTime, endTime);
                        });
                },
                // Remove days that are fully scheduled
                (date: Date) => {
                    return this.callbackAvailability
                        .find(d => this.isSameDay(DateTime.fromJSDate(date), d.date) && !d.isAvailable);
                },
            ]
        };
    }

    isDisabled(key: string) {
        return ['firstName', 'lastName', 'phoneNumber'].includes(key) && this.disable;
    }

    isSameDay(a: DateTime, b: DateTime): boolean {
        return a.year === b.year && a.month === b.month && a.day === b.day;
    }

    availabilityDateIsAllDay(startTime: DateTime, endTime: DateTime): boolean {
        return startTime.hour === 0 && startTime.minute === 0 && endTime.hour === 0 && endTime.minute === 0;
    }

    updateTimeRange(selectedDates: Date[]): void {
        if (!selectedDates || selectedDates.length === 0) {
            return;
        }
        const dateSelected = selectedDates[0] as Date;
        const availableDay = this.availability.schedule
            .find(s => s.dayOfWeek === (dateSelected).getDay());
        if (availableDay === undefined) {
            return;
        }
        const availability = this.callbackAvailability.find( d => this.isSameDay(DateTime.fromJSDate(selectedDates[0]), d.date));
        if (availability) {
            this.timeline = availability.availableTimes;
        }
    }

    scrollToDropDownItems(downDownItemsVisible: boolean) {
        setTimeout(() => {
            if (downDownItemsVisible) {
                const possibleDropDowns = document.getElementsByClassName('custom-dropdown');
                if (possibleDropDowns && possibleDropDowns.length > 0) {
                    const availableDropDown = possibleDropDowns[0];
                    availableDropDown.scrollIntoView({behavior: "smooth", block: "end", inline: "end"});
                }
            }
        }, 150, downDownItemsVisible);
    }

    buildAvailabilityObject() {
        for (let dayNumber = 0; dayNumber < this.availability.callBackDayRange; dayNumber++) {
            const dayAvailability = {} as CallbackAvailability;
            const workingDay = DateTime.local().plus({days: dayNumber}).setZone('America/Chicago').startOf('day');
            dayAvailability.date = workingDay;
            dayAvailability.availableTimes = [];

            const dayOverridden = this.availability.overrides
                .find(d => {
                    const startTime = DateTime.fromISO(d.overrideStartTime, {zone: 'America/Chicago'});
                    const endTime = DateTime.fromISO(d.overrideEndTime, {zone: 'America/Chicago'});
                    return this.isSameDay(workingDay, startTime) && this.availabilityDateIsAllDay(startTime, endTime) !== undefined;
                });
            const dayNotAvailable = this.availability.schedule
                .find(d => d.dayOfWeek === workingDay.weekday && d.startTime !== "" && d.endTime !== "") === undefined;
            const scheduledCalls = this.availability.scheduledCalls.filter( call => this.isSameDay(DateTime.fromISO(call.followUpRangeStart), workingDay)) as any[];
            const availableDay = this.availability.schedule
                .find(s => s.dayOfWeek === workingDay.weekday);
            if (availableDay === undefined) {
                continue;
            }
            const timeSlots = this.getAvailableTimesForDay(availableDay, workingDay.toJSDate());
            const workDayTimeSlotsWithScheduledCounts = scheduledCalls.reduce( (previousValue: TimeSlotCount[], currentValue: ScheduledCallbacks) => {
                const current = currentValue as ScheduledCallbacks;
                const scheduledTime = DateTime.fromISO(current.followUpRangeStart);
                if (previousValue.length) {
                    previousValue.forEach( (daySlot: TimeSlotCount) => daySlot.scheduledCount += this.timeRangeInSlotCount(daySlot.timeSlot, scheduledTime));
                } else {
                    previousValue.push(...this.buildTimeSlotCount(timeSlots, scheduledTime));
                }
                return previousValue;
            }, [] as TimeSlotCount[]) as TimeSlotCount[];

            if (dayOverridden || dayNotAvailable || !workDayTimeSlotsWithScheduledCounts.length) {
                dayAvailability.availableTimes = timeSlots.map( slot => this.formatTimeSlot(slot));
                dayAvailability.isAvailable = dayAvailability.availableTimes.length > 0;
                this.callbackAvailability.push(dayAvailability);
                continue;
            }
            const availableSlots = workDayTimeSlotsWithScheduledCounts.filter( (slot) => slot.scheduledCount < this.availability.maxCallbacksPerHour);
            dayAvailability.availableTimes = this.getAvailableTimes(availableSlots, timeSlots);
            dayAvailability.isAvailable = dayAvailability.availableTimes.length > 0;
            this.callbackAvailability.push(dayAvailability);
        }
    }

    getAvailableTimes(timeSlotCounts: TimeSlotCount[], timeSlots: TimeSlot[]) {
        if (timeSlotCounts) {
            return timeSlotCounts.map( slot => this.formatTimeSlot(slot.timeSlot));
        }
        return timeSlots.map( slot => this.formatTimeSlot(slot));
    }

    formatTimeSlot(timeSlot: TimeSlot) {
        return `${this.getFormattedTime(timeSlot.startDateTime)} - ${this.getFormattedTime(timeSlot.endDateTime)}`
    }

    getFormattedTime(dateTime: DateTime) {
        return dateTime.toFormat('h:mm a');
    }

    buildTimeSlotCount(timeSlots: TimeSlot[], scheduledTime: DateTime): TimeSlotCount[] {
        const mappedTimes = [] as TimeSlotCount[];
        for (const slot of timeSlots) {
            const count = this.timeRangeInSlotCount(slot, scheduledTime);
            const timeSlotCount = {} as TimeSlotCount;
            timeSlotCount.timeSlot = slot;
            timeSlotCount.scheduledCount = count;
            mappedTimes.push(timeSlotCount);
        }
        return mappedTimes;
    }

    timeRangeInSlotCount(timeSlot: TimeSlot, startDateTime: DateTime) {
        return timeSlot.startDateTime <= startDateTime && timeSlot.endDateTime > startDateTime ? 1 : 0;
    }


    getAvailableTimesForDay(dayAvailable: AvailabilitySchedule, dateSelected: Date): TimeSlot[] {
        const times = [] as TimeSlot[];
        if (dayAvailable.startTime === '' && dayAvailable.endTime === '') {
            return times;
        }
        const now = DateTime.local().setZone('America/Chicago');
        const isToday = this.isSameDay(DateTime.fromJSDate(dateSelected), now);

        const {startDate, endDate} = this.filterAvailableTimesByOverrideTimes(dayAvailable, dateSelected)
        let date = startDate
        while(date < endDate) {
            const startTime = date;
            const endTime = (date.plus({hour: this.callTimeHoursRange}) > endDate) ? endDate : date.plus({hour: this.callTimeHoursRange});
            const time = {} as TimeSlot;

            time.startDateTime = startTime;
            time.endDateTime = endTime;

            if (isToday && endTime < now) {
                date = date.plus({hour: this.callTimeHoursRange});
                continue;
            }
            times.push(time);
            date = date.plus({hour: this.callTimeHoursRange});
        }
        return times;
    }

    filterAvailableTimesByOverrideTimes(dayAvailable: AvailabilitySchedule, dateSelected: Date): {startDate: DateTime, endDate: DateTime } {
        const availableTimes = {
            startDate: this.getDateTimeFromDateAvailableAndSelected(dayAvailable.startTime, dateSelected),
            endDate: this.getDateTimeFromDateAvailableAndSelected(dayAvailable.endTime, dateSelected)
        };

        if(!this.availability.overrides || this.availability.overrides.length === 0) return availableTimes;

        const matchingOverrides = this.availability.overrides.filter(d => this.isSameDay(
            DateTime.fromJSDate(dateSelected, {zone: 'America/Chicago'}),
            DateTime.fromISO(d.overrideStartTime, {zone: 'America/Chicago'})
        ));

        if(!matchingOverrides || matchingOverrides.length === 0) return availableTimes;

        // Assumes we'll only ever have 1 override start/time per date
        return {
            startDate: DateTime.fromISO(matchingOverrides[0].overrideStartTime, {zone: 'America/Chicago'}),
            endDate: DateTime.fromISO(matchingOverrides[0].overrideEndTime, {zone: 'America/Chicago'}),
        }
    }

    getDateTimeFromDateAvailableAndSelected(time: string, dateSelected: Date) {
        return DateTime
            .fromJSDate(dateSelected, {zone: 'America/Chicago'})
            .set({
                hour: Number.parseInt(time.split(':')[0]),
                minute: Number.parseInt(time.split(':')[1])
            });
    }

    upperFirstChar(str: string): string {
        if (str) {
            return str.replace(/([A-Z])/g, ' $1')
            // uppercase the first character
            .replace(/^./, function(str){ return str.toUpperCase(); });
        }
        return '';
    }

    convertToKebabCase(str: string): string {
        if (str) {
            return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2')
            .replace(/([A-Z])([A-Z])(?=[a-z])/g, '$1-$2').toLowerCase();
        }
        return '';
    }

    timeRangeSelected(value: string) {
        this.data.timeRange = value;
    }

    getDateTimeFromSubmittedValue(timeInAmPm: string) {
        // DateTime should be in h:mm am format
        const parts = timeInAmPm.split(' ');
        const timeParts = parts[0].split(':');
        const minute = parseInt(timeParts[1], 10);
        let hour = parseInt(timeParts[0], 10);
        if (parts[1].trim().toLowerCase() !== 'am' && hour !== 12) {
            hour += 12;
        }
        return {
            hour: hour,
            minute: minute,
        } as DateObjectUnits
    }

    async submit() {
        const missingFields = this.formInputs
        .filter(item => !(item.value && item.value.replace(/\s/g, '')));

        if (missingFields.length) {
            this.errors = missingFields
            .reduce((acc, {key, label}) =>
                ({...acc, [key]: `Please provide a ${(label || '').toLowerCase()}.`}),
            {});
        }
        else if (!isEmailValid(this.data.email)) {
            this.errors = {email: 'Please provide a valid email.'};
        }
        else {
            const followUpRangeStart = DateTime.fromISO(this.data.date)
                .set(this.getDateTimeFromSubmittedValue(this.data.timeRange.split(' - ')[0]));
            const followUpRangeEnd = DateTime.fromISO(this.data.date)
                .set(this.getDateTimeFromSubmittedValue(this.data.timeRange.split(' - ')[1]));
            try {
                this.saving = true;
                const followUpBody = {
                    customerFirstName: this.data.firstName.toUpperCase(),
                    customerLastName: this.data.lastName.toUpperCase(),
                    customerPhone: this.data.phoneNumber,
                    customerEmail: this.data.email.toUpperCase(),
                    followUpRangeStart: followUpRangeStart.toISO(),
                    followUpRangeEnd: followUpRangeEnd.toISO(),
                    customerOrderId: this.$store.getters.getCartNumber(),
                    customerOrderToken: this.$store.state.shoppingCartData.token
                }
                if(this.isReschedule) {
                    await HTTP.post(`/public/customer-follow-up/${this.$store.getters.getCustomerInfo.followUpId}`, followUpBody);
                } else {
                    const response: {id: string} = await HTTP.post(`/public/customer-follow-up`, followUpBody);
                    this.$store.commit('setFollowUpId', response.id);
                }
                this.$store.commit('setDateForScheduledCall', this.data.date);
                this.$store.commit('setTimeRangeForScheduledCall', this.data.timeRange);
                this.$emit('success-event', this.data.email);
            } catch (error) {
                this.$notifyError("An error occurred. Customer Support will try to reach out to you.");
            } finally {
                this.saving = false;
            }
        }
    }
}
